import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";

import { AdaStatLink } from "@/components/adastat-link";
import { AssetLabel } from "@/components/asset-label";
import { type Column, DataList } from "@/components/data-list";
import { Pager, pageCount } from "@/components/pager";
import { QueryState } from "@/components/query-state";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { StatusBadges } from "@/features/opportunities/StatusBadges";
import { ada, chainTime, duration, route, windowEnd } from "@/features/opportunities/format";
import { pageSearch } from "@/features/opportunities/search";
import type { OpportunityDetail, OpportunityEvent, TradeSnapshot } from "@/generated/api/model";
import { useGetOpportunity } from "@/generated/api/opportunities/opportunities";
import { tokenIdOfPair } from "@/lib/cardanoscan";

export const Route = createFileRoute("/stats/$id")({ component: OpportunityPage, validateSearch: pageSearch });

// The trail arrives whole (the bot caps it at 1000 events), so it pages here, not in the API.
const TRAIL_PAGE_SIZE = 50;

function OpportunityPage() {
    const { id } = Route.useParams();
    const detail = useGetOpportunity(Number(id));

    return (
        <div className="space-y-3 md:space-y-4">
            <Link
                to="/stats/log"
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
                <ArrowLeft className="size-4" />
                Log
            </Link>
            <QueryState query={detail} isEmpty={() => false} emptyMessage={`No opportunity #${id}.`}>
                {(data) => <Detail detail={data} />}
            </QueryState>
        </div>
    );
}

function Detail({ detail }: { detail: OpportunityDetail }) {
    const { opportunity, events } = detail;
    const { window } = opportunity;
    const dropped = window.dropped_events;
    const navigate = Route.useNavigate();
    const page = Math.min(Route.useSearch().page ?? 1, pageCount(events.length, TRAIL_PAGE_SIZE));
    const shown = events.slice((page - 1) * TRAIL_PAGE_SIZE, page * TRAIL_PAGE_SIZE);

    return (
        <>
            <Card>
                <CardHeader>
                    <h2 className="min-w-0 truncate font-semibold">
                        #{window.id} <AssetLabel id={tokenIdOfPair(window.pair)} name={window.token_name} />
                    </h2>
                    <StatusBadges view={opportunity} />
                </CardHeader>
                <CardContent>
                    <dl className="grid grid-cols-2 gap-x-3 gap-y-2.5 text-[0.9375rem] sm:grid-cols-3 lg:grid-cols-4">
                        <Field label="Opened" value={chainTime(window.opened_at)} />
                        <Field
                            label={window.status === "open" ? "Last seen" : "Closed"}
                            value={chainTime(windowEnd(window))}
                        />
                        <Field label="Open for" value={duration(opportunity.duration_secs)} />
                        <Field
                            label="Slots"
                            value={`${window.opened_slot} → ${window.closed_slot ?? window.last_seen_slot}`}
                        />
                        <Field label="First profit" value={`${ada(window.first_profit_lovelace)} ADA`} />
                        <Field label="Peak profit" value={`${ada(window.peak_profit_lovelace)} ADA`} />
                        <Field label="Last profit" value={`${ada(window.last_profit_lovelace)} ADA`} />
                        <Field
                            label="Events"
                            value={
                                dropped > 0
                                    ? `${window.recorded_events} (+${dropped} not stored)`
                                    : String(window.recorded_events)
                            }
                        />
                        <Field label="Pair" value={window.pair} wide />
                    </dl>
                </CardContent>
            </Card>
            <Card>
                <CardHeader>
                    <h2 className="font-semibold">Trade at the peak</h2>
                </CardHeader>
                <CardContent>
                    <Trade trade={window.peak} />
                </CardContent>
            </Card>
            <Card>
                <CardHeader>
                    <h2 className="font-semibold">Event trail</h2>
                    <span className="text-xs text-muted-foreground">chain time</span>
                </CardHeader>
                <CardContent className="px-2 sm:px-4">
                    <DataList
                        rows={shown}
                        columns={EVENT_COLUMNS}
                        rowKey={(event) => String(event.seq)}
                        label="Event trail"
                    />
                    <Pager
                        page={page}
                        total={events.length}
                        pageSize={TRAIL_PAGE_SIZE}
                        noun="events"
                        onPage={(to) => navigate({ search: { page: to }, resetScroll: false })}
                    />
                </CardContent>
            </Card>
        </>
    );
}

function Field({ label, value, wide = false }: { label: string; value: string; wide?: boolean }) {
    return (
        <div className={wide ? "col-span-full min-w-0" : "min-w-0"}>
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
            <dd className="break-all">{value}</dd>
        </div>
    );
}

function Trade({ trade }: { trade: TradeSnapshot }) {
    return (
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2.5 text-[0.9375rem] sm:grid-cols-3">
            <Field label="Route" value={route(trade)} />
            <Field label="Buy for" value={`${ada(trade.buy_amount_lovelace)} ADA`} />
            <Field label="Tokens" value={trade.token_amount} />
            <Field label="Sell for" value={`${ada(trade.sell_receive_lovelace)} ADA`} />
            <Field label="Buy pool TVL" value={`${ada(trade.buy_pool_tvl_lovelace)} ADA`} />
            <Field label="Sell pool TVL" value={`${ada(trade.sell_pool_tvl_lovelace)} ADA`} />
            <div className="col-span-full min-w-0">
                <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Buy pool UTxO</dt>
                <dd>
                    <AdaStatLink kind="utxo" value={trade.buy_pool_utxo} label="Buy pool UTxO" />
                </dd>
            </div>
            <div className="col-span-full min-w-0">
                <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Sell pool UTxO</dt>
                <dd>
                    <AdaStatLink kind="utxo" value={trade.sell_pool_utxo} label="Sell pool UTxO" />
                </dd>
            </div>
        </dl>
    );
}

const EVENT_COLUMNS: Column<OpportunityEvent>[] = [
    { label: "Event", value: (event) => event.kind, title: true },
    {
        label: "Profit",
        value: (event) => (event.profit_lovelace == null ? null : `${ada(event.profit_lovelace)} ADA`),
        title: true,
    },
    { label: "Time", value: (event) => chainTime(event.at), wide: true },
    { label: "Slot", value: (event) => event.slot, className: "tabular-nums" },
    { label: "Note", value: (event) => event.note ?? null, wide: true },
    { label: "Route", value: (event) => (event.trade ? route(event.trade) : null) },
    { label: "Buy for", value: (event) => (event.trade ? `${ada(event.trade.buy_amount_lovelace)} ADA` : null) },
    {
        label: "Pools",
        wide: true,
        render: (event) =>
            event.trade ? (
                <span className="flex flex-col gap-1">
                    <AdaStatLink kind="utxo" value={event.trade.buy_pool_utxo} label="Buy pool UTxO" fit="table" />
                    <AdaStatLink kind="utxo" value={event.trade.sell_pool_utxo} label="Sell pool UTxO" fit="table" />
                </span>
            ) : (
                "—"
            ),
    },
];
